import { useEffect } from 'react'
import { User, Edit, Camera } from 'lucide-react'
import { useCoachStore } from '@/stores/coachStore'

export function ProfilePage() {
  const { coach, setCurrentPage } = useCoachStore()

  useEffect(() => {
    setCurrentPage('profile')
  }, [setCurrentPage])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="coach-header">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600">
            Manage your coaching profile and information
          </p>
        </div>
        
        <button className="coach-button-primary px-4 py-2">
          <Edit className="w-4 h-4 mr-2" />
          Edit Profile
        </button>
      </div>

      {/* Profile Summary */}
      <div className="coach-section">
        <div className="flex items-center space-x-6">
          <div className="relative">
            <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center">
              {coach?.profilePhoto ? (
                <img 
                  src={coach.profilePhoto} 
                  alt={coach.name} 
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 text-primary-600" />
              )}
            </div>
            <button className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-md border border-gray-200 hover:bg-gray-50">
              <Camera className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          
          <div>
            <h2 className="text-xl font-bold text-gray-900">{coach?.name || 'Coach Name'}</h2>
            <p className="text-gray-600">{coach?.email || 'coach@example.com'}</p>
            <div className="flex items-center space-x-4 mt-2">
              <span className="status-active">Active Coach</span>
              <span className="text-sm text-gray-500">
                Member since {coach?.createdAt ? new Date(coach.createdAt).getFullYear() : '2024'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="coach-section">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
          <div className="space-y-4">
            <div>
              <label className="coach-label">Full Name</label>
              <input 
                type="text" 
                value={coach?.name || ''} 
                readOnly 
                className="coach-input bg-gray-50" 
              />
            </div>
            <div>
              <label className="coach-label">Email</label>
              <input 
                type="email" 
                value={coach?.email || ''} 
                readOnly 
                className="coach-input bg-gray-50" 
              />
            </div>
            <div>
              <label className="coach-label">Phone Number</label>
              <input 
                type="tel" 
                placeholder="Add phone number" 
                className="coach-input" 
              />
            </div>
            <div>
              <label className="coach-label">Timezone</label>
              <input 
                type="text" 
                value={coach?.timezone || ''} 
                readOnly 
                className="coach-input bg-gray-50" 
              />
            </div>
          </div>
        </div>

        <div className="coach-section">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h3>
          <div className="space-y-4">
            <div>
              <label className="coach-label">Bio</label>
              <textarea 
                rows={4}
                placeholder="Tell clients about your coaching philosophy and experience..."
                className="coach-input"
              />
            </div>
            <div>
              <label className="coach-label">Specializations</label>
              <div className="flex flex-wrap gap-2">
                <span className="status-badge bg-primary-100 text-primary-800">Strength Training</span>
                <span className="status-badge bg-primary-100 text-primary-800">Weight Loss</span>
                <button className="status-badge bg-gray-100 text-gray-800 hover:bg-gray-200">
                  + Add Specialization
                </button>
              </div>
            </div>
            <div>
              <label className="coach-label">Certifications</label>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-900">NASM Certified Personal Trainer</span>
                  <span className="text-xs text-success-600">Verified</span>
                </div>
                <button className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400">
                  + Add Certification
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}